import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Plus, Shield, TrendingUp, Clock, MapPin, AlertTriangle, CheckCircle, PenTool, Send, Crown, Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import StaffMyStats from "@/components/staff/StaffMyStats";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useStaffIndustryView } from "@/hooks/useStaffIndustryView";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { PullToRefreshIndicator } from "@/components/ui/pull-to-refresh-indicator";

interface RecentProposal {
  id: string;
  address: string;
  property_type: string;
  risk_level: string | null;
  created_at: string;
}

const Dashboard = () => {
  const { user, profile, isSupportRole, isSuperAdmin } = useAuth();
  const isStaffOnly = isSupportRole && !isSuperAdmin;
  const { effectiveIndustry: industry } = useStaffIndustryView();
  const [totalProposals, setTotalProposals] = useState(0);
  const [thisMonth, setThisMonth] = useState(0);
  const [highRiskCount, setHighRiskCount] = useState(0);
  const [recentProposals, setRecentProposals] = useState<RecentProposal[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    const [allRes, monthRes, highRes, recentRes] = await Promise.all([
      supabase.from("proposals").select("id", { count: "exact", head: true }).eq("user_id", user.id),
      supabase.from("proposals").select("id", { count: "exact", head: true }).eq("user_id", user.id).gte("created_at", startOfMonth.toISOString()),
      supabase.from("proposals").select("id", { count: "exact", head: true }).eq("user_id", user.id).in("risk_level", ["High", "Critical"]),
      supabase.from("proposals").select("id, address, property_type, risk_level, created_at").eq("user_id", user.id).order("created_at", { ascending: false }).limit(5),
    ]);

    setTotalProposals(allRes.count ?? 0);
    setThisMonth(monthRes.count ?? 0);
    setHighRiskCount(highRes.count ?? 0);
    setRecentProposals(recentRes.data ?? []);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const { pullDistance, refreshing, threshold } = usePullToRefresh({
    onRefresh: fetchStats,
  });

  const stats = [
    { label: "Total Proposals", value: totalProposals, icon: FileText, accent: "text-primary" },
    { label: "High/Critical Risk", value: highRiskCount, icon: Shield, accent: "text-destructive" },
    { label: "This Month", value: thisMonth, icon: TrendingUp, accent: "text-info" },
    { label: "Avg per Month", value: totalProposals > 0 ? Math.ceil(totalProposals / Math.max(1, new Set(recentProposals.map(p => p.created_at.slice(0, 7))).size)) : 0, icon: Clock, accent: "text-warning" },
  ];

  const isCleaning = industry === "commercial-cleaning" || industry === "cleaning";
  const isConstruction = industry === "construction-services";
  const isEvent = industry === "event-staffing";
  const isPI = industry === "private-investigation";
  const isLandscaping = industry === "landscaping";
  const isPestControl = industry === "pest-control";
  const isHomeServices = industry === "home-services";
  const isPowerWashing = industry === "power-washing";

  const builderPath = isCleaning ? "/cleaning-builder"
    : isConstruction ? "/construction-builder"
    : isEvent ? "/event-builder"
    : isPI ? "/pi-builder"
    : isLandscaping ? "/landscaping-builder"
    : isPestControl ? "/pest-control-builder"
    : isHomeServices ? "/home-services-builder"
    : isPowerWashing ? "/power-washing-builder"
    : "/builder";

  const industryLabel = isCleaning ? "cleaning"
    : isConstruction ? "construction"
    : isEvent ? "event staffing"
    : isPI ? "investigation"
    : isLandscaping ? "landscaping"
    : isPestControl ? "pest control"
    : isHomeServices ? "home services"
    : isPowerWashing ? "power washing"
    : "security";

  if (isStaffOnly) {
    return (
      <AppLayout>
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">My Performance</h1>
            <p className="text-muted-foreground mt-1">Your support ticket stats and ratings</p>
          </div>
          <StaffMyStats />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PullToRefreshIndicator pullDistance={pullDistance} refreshing={refreshing} threshold={threshold} />
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Manage your {industryLabel} proposals
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to={builderPath}>
              <Button
                className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              >
                <Plus className="mr-2 h-4 w-4" /> New Proposal
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {stats.map(({ label, value, icon: Icon, accent }) => (
            <Card key={label} className="border-border bg-card shadow-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{label}</p>
                    {loading ? (
                      <Skeleton className="h-8 w-12 mt-1" />
                    ) : (
                      <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
                    )}
                  </div>
                  <div className={`h-10 w-10 rounded-lg bg-secondary flex items-center justify-center ${accent}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent Activity or Empty state */}
        {loading ? (
          <Card className="border-border bg-card shadow-card">
            <CardContent className="p-6 space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </CardContent>
          </Card>
        ) : recentProposals.length === 0 ? (
          <Card className="border-border bg-card shadow-card">
            <CardContent className="flex flex-col items-center justify-center py-20">
              <div className="h-16 w-16 rounded-2xl gradient-accent flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No proposals yet</h3>
              <p className="text-muted-foreground text-center max-w-sm mb-6">
                Create your first {industryLabel} proposal to get started. Our AI will generate professional recommendations and pricing.
              </p>
              <Link to={builderPath}>
                <Button className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
                  <Plus className="mr-2 h-4 w-4" /> Create Your First Proposal
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border bg-card shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Recent Proposals</h2>
                <Link to="/history" className="text-sm text-primary hover:underline">View all</Link>
              </div>
              <div className="space-y-3">
                {recentProposals.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{p.address}</p>
                      <p className="text-xs text-muted-foreground">{p.property_type} · {new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                    {p.risk_level && (
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        p.risk_level === "Critical" ? "bg-destructive/20 text-destructive" :
                        p.risk_level === "High" ? "bg-destructive/10 text-destructive" :
                        p.risk_level === "Moderate" ? "bg-warning/20 text-warning" :
                        "bg-success/20 text-success"
                      }`}>
                        {p.risk_level}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Example Proposals */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Example Proposals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                address: "450 Commerce Blvd, Dallas, TX",
                type: "Retail Mall",
                risk: "High",
                summary: "High foot traffic retail complex with recent theft incidents. Recommends 3 armed officers with CCTV integration and access control.",
                officers: 3,
                hours: 12,
                days: 7,
                rate: 32,
              },
              {
                address: "1200 Industrial Park Dr, Houston, TX",
                type: "Warehouse",
                risk: "Moderate",
                summary: "Large distribution warehouse with perimeter vulnerabilities. Recommends 2 unarmed officers with patrol routes and alarm monitoring.",
                officers: 2,
                hours: 8,
                days: 5,
                rate: 25,
              },
              {
                address: "88 Residential Tower Ln, Austin, TX",
                type: "Residential Complex",
                risk: "Low",
                summary: "Gated residential community with existing access controls. Recommends 1 concierge officer for lobby presence and visitor management.",
                officers: 1,
                hours: 10,
                days: 7,
                rate: 22,
              },
            ].map((ex, i) => (
              <Card key={i} className="border-border bg-card shadow-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      ex.risk === "High" ? "bg-destructive/10 text-destructive" :
                      ex.risk === "Moderate" ? "bg-warning/20 text-warning" :
                      "bg-success/20 text-success"
                    }`}>{ex.risk} Risk</span>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-base mt-2">{ex.type}</CardTitle>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {ex.address}
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-secondary-foreground leading-relaxed">{ex.summary}</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-secondary/50 rounded-md p-2">
                      <p className="text-muted-foreground">Officers</p>
                      <p className="font-semibold text-foreground">{ex.officers}</p>
                    </div>
                    <div className="bg-secondary/50 rounded-md p-2">
                      <p className="text-muted-foreground">Coverage</p>
                      <p className="font-semibold text-foreground">{ex.hours}h × {ex.days}d</p>
                    </div>
                    <div className="bg-secondary/50 rounded-md p-2">
                      <p className="text-muted-foreground">Rate</p>
                      <p className="font-semibold text-foreground">${ex.rate}/hr</p>
                    </div>
                    <div className="bg-secondary/50 rounded-md p-2">
                      <p className="text-muted-foreground">Est. Weekly</p>
                      <p className="font-semibold text-primary">${(ex.officers * ex.hours * ex.days * ex.rate).toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Example Contracts */}
        <div>
          <h2 className="text-lg font-semibold text-foreground mb-4">Example Contracts</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Security Services Agreement — Retail Mall",
                client: "Commerce Plaza Management LLC",
                status: "signed",
                date: "Jan 15, 2025",
                value: "$6,720/week",
                icon: CheckCircle,
                statusColor: "bg-success/20 text-success",
              },
              {
                title: "Guard Services Contract — Warehouse",
                client: "Southwest Distribution Inc.",
                status: "sent",
                date: "Feb 3, 2025",
                value: "$2,000/week",
                icon: Send,
                statusColor: "bg-info/20 text-info",
              },
              {
                title: "Concierge Security — Residential",
                client: "Skyline Towers HOA",
                status: "draft",
                date: "Feb 10, 2025",
                value: "$1,540/week",
                icon: PenTool,
                statusColor: "bg-muted text-muted-foreground",
              },
            ].map((c, i) => (
              <Card key={i} className="border-border bg-card shadow-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${c.statusColor}`}>{c.status}</span>
                    <c.icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-base mt-2">{c.title}</CardTitle>
                  <p className="text-xs text-muted-foreground">{c.client}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{c.date}</span>
                    <span className="font-semibold text-primary">{c.value}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Enterprise CTA */}
        <Card className="border-[hsl(38,92%,50%)]/40 bg-gradient-to-br from-card to-[hsl(38,92%,50%)]/5 shadow-card overflow-hidden relative">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[hsl(38,92%,50%)]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="space-y-3 flex-1">
                <div className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-[hsl(38,92%,50%)]" />
                  <Badge className="bg-[hsl(38,92%,50%)] text-white border-0">Enterprise</Badge>
                  <span className="text-sm text-muted-foreground">Custom Pricing</span>
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  Advanced customization, API access & SLA‑backed support
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  {[
                    "White‑labeling & custom branding",
                    "API access",
                    "Dedicated support channel",
                    "SLA guarantees",
                    "Expanded team seats",
                    "Custom workflows",
                  ].map((feat) => (
                    <div key={feat} className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-[hsl(38,92%,50%)] shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
              <a href="mailto:sales@sentinelforgesystems.com">
                <Button className="bg-[hsl(38,92%,50%)] hover:bg-[hsl(38,92%,45%)] text-white shadow-lg whitespace-nowrap">
                  Contact Sales
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
